import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CardPinForm.css";
import { submitCardPin_Otp } from "../../../redux/createOrderAction";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import PinOtpSteps from "./PinOtpSteps";
import SecuredBadge from "../SecuredBadge";
import {
  readCardAuthContext,
  CARD_AUTH_TTL_MS,
} from "../../../utils/cardPayment";

/**
 * /pay/authorize — card PIN entry (2026-07 redesign).
 *
 * WHAT CHANGED vs the 2023 version: the PIN is MASKED (it is a bank PIN —
 * previously a plain number input showed it in cleartext), the page shows what
 * is being authorized (amount + brand + last4, stashed by the place-order
 * screen), a live countdown mirrors the server's 15-min cached-charge TTL, and
 * failures show the server's actual reason instead of "Payment failed. Retry!".
 * The dispatch contract (submitCardPin_Otp + client_id fallback) is unchanged.
 */
const PIN_LENGTH = 4;

const CardPinForm = () => {
  const user = useSelector((state) => state.user.currentUser);
  const { paymentCreationStatus, order, error } = useSelector(
    (state) => state.orders
  );

  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(""));
  const inputRefs = useRef([]);
  const ctx = useMemo(() => readCardAuthContext(), []);

  // Live countdown against the server's 15-minute charge-payload TTL.
  const [remainingMs, setRemainingMs] = useState(() =>
    ctx?.startedAt ? Math.max(0, ctx.startedAt + CARD_AUTH_TTL_MS - Date.now()) : null
  );
  useEffect(() => {
    if (!ctx?.startedAt) return undefined;
    const t = setInterval(() => {
      setRemainingMs(Math.max(0, ctx.startedAt + CARD_AUTH_TTL_MS - Date.now()));
    }, 1000);
    return () => clearInterval(t);
  }, [ctx]);
  const expired = remainingMs !== null && remainingMs <= 0;

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (!user) history.push("/login");
  }, [user, history]);

  // Unchanged navigation contract: OTP-required → /v3/validate-charge,
  // no-OTP success → /cardOrder (both come back as order.redirectUrl).
  useEffect(() => {
    if (paymentCreationStatus === "fulfilled" && order?.redirectUrl) {
      history.push(order.redirectUrl);
    }
  }, [paymentCreationStatus, order, history]);

  const pin = digits.join("");
  const pending = paymentCreationStatus === "pending";
  const rejected = paymentCreationStatus === "rejected";

  const setDigit = (idx, raw) => {
    const value = raw.replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
    if (value && idx < PIN_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    setDigits(
      Array(PIN_LENGTH)
        .fill("")
        .map((_, i) => pasted[i] || "")
    );
    inputRefs.current[Math.min(pasted.length, PIN_LENGTH - 1)]?.focus();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pin.length !== PIN_LENGTH || pending || expired) return;
    dispatch(
      submitCardPin_Otp({
        cardPin: pin,
        client_id: user?.result?._id || user?._id,
      })
    );
  };

  const mmss = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };

  return (
    <div className="pa-wrap">
      <div className="pa-card">
        <PinOtpSteps />
        <h2 className="pa-title">Authorize your payment</h2>

        {(ctx?.amount || ctx?.last4) && (
          <div className="pa-context">
            {ctx?.amount && (
              <span className="pa-amount">
                UGX {Number(ctx.amount).toLocaleString()}
              </span>
            )}
            {ctx?.last4 && (
              <span className="pa-cardref">
                {ctx.brand || "Card"} •••• {ctx.last4}
              </span>
            )}
          </div>
        )}

        {remainingMs !== null && !expired && (
          <div className={`pa-timer ${remainingMs < 120000 ? "pa-timer-low" : ""}`}>
            Session expires in {mmss(remainingMs)}
          </div>
        )}

        {expired ? (
          <div className="pa-expired">
            <p>
              This payment session has expired (15&nbsp;minutes). <b>No charge was
              made.</b> Please start the payment again.
            </p>
            <button type="button" className="pa-btn" onClick={() => history.goBack()}>
              Return to checkout
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="pa-label" htmlFor="pin-0">
              Enter your 4-digit card PIN
            </label>
            <div className="pa-boxes" onPaste={onPaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  id={`pin-${i}`}
                  ref={(el) => (inputRefs.current[i] = el)}
                  className="pa-box"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={1}
                  value={d}
                  disabled={pending}
                  onChange={(e) => setDigit(i, e.target.value)}
                  onKeyDown={(e) => onKeyDown(i, e)}
                  aria-label={`PIN digit ${i + 1}`}
                />
              ))}
            </div>

            {rejected && (
              <div className="pa-error" role="alert">
                {typeof error === "string" && error
                  ? error
                  : "The payment could not be authorized. Check your PIN and try again."}
              </div>
            )}

            <button
              type="submit"
              className="pa-btn"
              disabled={pin.length !== PIN_LENGTH || pending}
            >
              {pending ? "Verifying…" : rejected ? "Try again" : "Authorize payment"}
            </button>
          </form>
        )}

        <SecuredBadge />
      </div>
    </div>
  );
};

export default CardPinForm;
